import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { NavigationStateService } from '@mwe/services';
import { catchError, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { homeRoutePrefix } from '@shared-ikp/app.constants';
import { IkpPopupService } from '@shared-ikp/service/popup/popup.service';
import { Company } from '@shared-ikp/model/company.model';
import { ROUTE_NOT_FOUND } from '../../app.constants';
import { UMMELDUNG_OPEN_ERROR_POPUP } from '../../config/popup/ummeldung-popup.config';
import { UmmeldungResponse } from '../../../generated/model/ummeldung-response.model';
import { MeterPoint } from '../../model/meter-point.model';
import { CompanyService } from '../../service/company/company.service';
import { MeterPointService } from '../../service/meter-point/meter-point.service';
import { UmmeldungService } from '../../service/ummeldung/ummeldung.service';
import { findCompanyByGpNr } from '../../service/company/company.utils';
import { getUmmeldungWizardStartDataFromState, UmmeldungStoreType, UmmeldungWizardStartData } from './models';
import { UmmeldungStore } from './ummeldung.store';
import { buildUmmeldungWizardData } from './utils';

/**
 * Loads everything a wizard session runs on — Zählpunkt, Ummeldung and Unternehmen des Accounts,
 * verwaltete Unternehmen and Unternehmen which owns the Zählpunkt and blocks activation of the wizard
 * route until it is all in the store.
 */
export const ummeldungWizardStartResolver: ResolveFn<boolean> = () => {
  const store = inject(UmmeldungStore);
  const router = inject(Router);
  const navigation = inject(NavigationStateService);
  const meterPointService = inject(MeterPointService);
  const ummeldungService = inject(UmmeldungService);
  const companyService = inject(CompanyService);
  const popupService = inject(IkpPopupService);

  const leaveWizard = () => new RedirectCommand(router.parseUrl(navigation.wizardCancelUrl));
  const navigationStart = getUmmeldungWizardStartDataFromState(router.currentNavigation()?.extras.state ?? history.state);
  const startData = navigationStart ?? restoredStartData(store);
  if (!startData) {
    store.clearStore();
    return of(new RedirectCommand(router.parseUrl(homeRoutePrefix(ROUTE_NOT_FOUND))));
  }
  const restoredDraft = navigationStart ? null : (store.activeUmmeldung() ?? null);

  return forkJoin({
    zaehlpunkt: meterPointService.loadMeterPoint(startData.vertragsnummer, startData.zaehlpunktId),
    loadedUmmeldung: loadUmmeldung(ummeldungService, startData.editableUmmeldungId),
    accountUnternehmen: from(companyService.loadAllCompanies()),
    verwalteteUnternehmen: companyService.loadManagedCompanies(),
  }).pipe(
    switchMap(({ zaehlpunkt, loadedUmmeldung, accountUnternehmen, verwalteteUnternehmen }) =>
      resolveZaehlpunktUnternehmen(companyService, zaehlpunkt, accountUnternehmen).pipe(
        map(zaehlpunktUnternehmen =>
          buildUmmeldungWizardData({
            zaehlpunkt,
            loadedUmmeldung,
            restoredDraft,
            uploadMode: startData.mode === 'upload',
            zaehlpunktUnternehmen,
            accountUnternehmen,
            verwalteteUnternehmen,
          }),
        ),
      ),
    ),
    map(data => {
      store.initialize(data);
      return true;
    }),
    catchError(error => {
      console.error('Error opening ummeldung wizard:', error);
      store.clearStore();
      popupService.handleGenericPopup(UMMELDUNG_OPEN_ERROR_POPUP, true);
      return of(leaveWizard());
    }),
  );
};

function restoredStartData(store: UmmeldungStoreType): UmmeldungWizardStartData | null {
  const zaehlpunkt = store.aktuellerZaehlpunkt();
  if (!zaehlpunkt?.vertragsnummer || !zaehlpunkt.zaehlpunktId) {
    return null;
  }

  return {
    vertragsnummer: zaehlpunkt.vertragsnummer,
    zaehlpunktId: zaehlpunkt.zaehlpunktId,
    editableUmmeldungId: store.activeUmmeldung()?.id,
    // uploadMode is session-storage synced, so a reload inside the upload step stays in upload mode.
    mode: store.uploadMode() ? 'upload' : 'edit',
  };
}

function loadUmmeldung(ummeldungService: UmmeldungService, id: string | undefined): Observable<UmmeldungResponse | null> {
  if (!id) {
    return of(null);
  }

  return ummeldungService.getUmmeldung({ id }).pipe(map(response => ({ ...response, ummeldung: { id, ...response.ummeldung } })));
}

function resolveZaehlpunktUnternehmen(
  companyService: CompanyService,
  zaehlpunkt: MeterPoint,
  accountUnternehmen: Company[],
): Observable<Company | null> {
  if (!zaehlpunkt.geschaeftspartnerNr) {
    return of(null);
  }

  const accountCompany = findCompanyByGpNr(accountUnternehmen, zaehlpunkt.geschaeftspartnerNr);
  if (accountCompany) {
    return of(accountCompany);
  }

  return from(companyService.loadCompanyByGpNr(zaehlpunkt.geschaeftspartnerNr));
}
